import { CSSObject } from '@emotion/styled';
import {
  getResponsiveStyles as sherpaGetResponsiveStyles,
  ResponsiveValue,
} from '../../deps/@bshgroup/component-themes';
import { breakpoints } from '../global/global-config';

export type Orientation = 'auto' | 'vertical' | 'horizontal';
export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const breakpointKeys: Breakpoint[] = ['sm', 'md', 'lg', 'xl', 'xxl'];

export default class AutoOrientationService {
  static getResponsiveStyles<V>(
    value: ResponsiveValue<V>,
    styles: (value: V) => CSSObject,
    options?: { applicationContent?: boolean },
  ) {
    const mediaStyles = sherpaGetResponsiveStyles(value, styles);

    return {
      ...mediaStyles,
      ...(options?.applicationContent === false
        ? {}
        : Object.fromEntries(
            Object.entries(mediaStyles).map(([key, value]) => [
              key.replace('@media', '@container application-content'),
              value,
            ]),
          )),
    };
  }

  static getStyles(
    verticalStyles: CSSObject,
    horizontalStyles: CSSObject,
    orientation: Orientation = 'auto',
    options?: { applicationContent?: boolean },
  ): CSSObject {
    return orientation === 'auto'
      ? this.getResponsiveStyles(
          {
            [breakpoints.sm]: 'sm',
            [breakpoints.md]: 'lg',
          },
          size => (size === 'sm' ? verticalStyles : horizontalStyles),
          options,
        )
      : orientation === 'vertical'
        ? verticalStyles
        : horizontalStyles;
  }

  static breakpointStyles(breakpoint: Breakpoint, smallStyles: CSSObject, largeStyles: CSSObject): CSSObject {
    return this.getResponsiveStyles(
      {
        [breakpoints.sm]: 'sm',
        [breakpoints[breakpoint]]: 'lg',
      },
      size => (size === 'sm' ? smallStyles : largeStyles),
    );
  }

  static multiBreakpointStyles(styles: Partial<{ [breakpoint in Breakpoint]: CSSObject }>): CSSObject {
    return this.getResponsiveStyles(
      Object.fromEntries(breakpointKeys.map(e => [breakpoints[e], e])),
      size =>
        breakpointKeys
          .slice(0, breakpointKeys.indexOf(size) + 1)
          .map(e => styles[e])
          .at(-1) ?? {},
    );
  }

  static getBreakpoint() {
    const width = window.innerWidth;
    return (
      Object.entries(breakpoints)
        .sort((a, b) => a[1] - b[1])
        .findLast(e => width > e[1])?.[0] ?? 'xxl'
    );
  }

  static isMobile() {
    return this.getBreakpoint() === 'sm';
  }
}
